import React, { useContext } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import kebabCase from 'lodash.kebabcase';
// @ts-ignore
import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import LayoutContext from '../../contexts/layoutContext';

import Link from '../link/link';

import './navbar-link.scss';

import { Drupal_ParagraphMarketoForm } from '../../generated/graphql';

interface Sublink {
  url: string;
  label: string;
  fieldKmEventName: string;
}

export interface LinkInterface {
  label?: string;
  sublinks?: Sublink[];
  fieldLinkPhoneNumber?: string;
  type?: string;
  url?: string;
  fieldKey?: string;
  fieldMarketo?: Drupal_ParagraphMarketoForm;
  fieldKmEventName?: string;
  truncate?: boolean;
}

interface MenuItem {
  link: LinkInterface;
  isVisio?: boolean;
  visioQueryParams: string;
  openSubMenu: string;
  useButtons?: boolean;
  toggleSubMenu: (label?: string, checkWidth?: boolean) => void;
  darkButtons?: boolean;
}

const NavbarLink = ({
  link: {
    label,
    sublinks,
    fieldLinkPhoneNumber,
    type,
    url,
    fieldKey,
    fieldMarketo,
    fieldKmEventName,
    truncate
  },
  isVisio,
  visioQueryParams,
  openSubMenu,
  useButtons,
  toggleSubMenu,
  darkButtons
}: MenuItem) => {
  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_nav_back
              }
            }
          }
        }
      }
    `
  );

  const {
    language: { langPrefix, i18nKey },
    marketo: { getMarketoModal, getMarketoFields }
  } = useContext(LayoutContext);

  const i18n = i18nFunct(i18nData, i18nKey);

  const buttonClasses = useButtons
    ? 'button chart chart--small navbar-link__button'
    : 'navbar-link__link';
  const typeClasses = type ? `chart--${type}` : '';
  const darkClasses = darkButtons ? 'chart--dark' : '';
  const truncateClass = truncate ? 'chart--truncate' : '';

  if (!sublinks || sublinks.length === 0) {
    let href =
      fieldLinkPhoneNumber && fieldLinkPhoneNumber.trim().length
        ? `tel:${fieldLinkPhoneNumber}`
        : url;
    href =
      isVisio && fieldKey === 'pricing' ? `${href}${visioQueryParams}` : href;
    if (fieldMarketo) {
      const { mktoId, mktoFields } = getMarketoFields(fieldMarketo, langPrefix);
      if (mktoId) {
        return (
          <div className="navbar-link__wrap">
            <button
              type="button"
              className={`${buttonClasses} ${typeClasses} ${darkClasses}`}
              data-km-trigger={`nav-${kebabCase(fieldKmEventName)}`}
              onClick={() => getMarketoModal(mktoId, mktoFields)}
              key={url}
            >
              {label}
            </button>
          </div>
        );
      }
    }
    return (
      <div className="navbar-link__wrap">
        <Link
          className={`${buttonClasses} ${typeClasses} ${darkClasses} js-phone-number`}
          data-km-trigger={`nav-${kebabCase(fieldKmEventName)}`}
          onMouseOver={() => toggleSubMenu('', true)}
          to={href!}
          key={url}
        >
          {label}
        </Link>
      </div>
    );
  }
  return (
    <div
      className="navbar-link__wrap"
      data-km-trigger={`nav-${kebabCase(fieldKmEventName)}`}
      data-menu-target={`${kebabCase(label)}-submenu`}
      key={url}
    >
      <button
        className={`${buttonClasses} ${typeClasses} ${darkClasses} ${truncateClass}`}
        onClick={() => toggleSubMenu(label)}
        onKeyDown={() => toggleSubMenu(label)}
        onMouseOver={() => toggleSubMenu(label, true)}
        onFocus={() => toggleSubMenu(label)}
        role="button"
        tabIndex={0}
      >
        {label}
      </button>
      <div
        className={`navbar-link__subnav ${
          label === openSubMenu ? 'navbar-link__subnav--open' : ''
        }`}
      >
        <div
          className={`navbar-link__submenu ${kebabCase(label)}-submenu`}
          data-km-trigger={`nav-${kebabCase(fieldKmEventName)}`}
        >
          <div className="navbar-link__mobile-menu">
            <div
              className="navbar-link__mobile-back"
              data-km-trigger="nav-mobile-back"
              onClick={() => toggleSubMenu()}
              onKeyDown={() => toggleSubMenu()}
              role="button"
              tabIndex={0}
            >
              ◀ &nbsp;
              {i18n('mktg-nav-back')}
            </div>
            <div className="navbar-link__mobile-title">{label}</div>
          </div>
          <ul className="navbar-link__sublinks">
            {sublinks &&
              sublinks.map(sublink => (
                <li className="navbar-link__sublink-item" key={sublink.url}>
                  <Link
                    className="navbar-link__sublink js-phone-number"
                    data-km-trigger={`nav-${kebabCase(
                      sublink.fieldKmEventName
                    )}`}
                    to={sublink.url}
                  >
                    {sublink.label}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NavbarLink;
